/* ============================================================
   FitLibrary — fitlibrary-ebook-bundle.js
   Full eBook Bundle page. PHASE 1 scope: the
   four-eBook Explorer tablist and NOTHING else.

   Deliberately absent, because the theme already owns them:
     · header / mobile navigation  → fitlibrary-header.js
     · FAQ accordions              → fitlibrary-global.js
     · product form / add-to-cart  → fitlibrary-product.js
   No second controller is created for any of those.

   The controller is registered through FL.register so it also
   re-runs on shopify:section:load and stays live in the theme
   editor, and it is scoped PER SECTION INSTANCE: every tablist
   keeps its own tab array, so two Explorer sections on one page
   could never steer each other.

   Keyboard model — WAI-ARIA Authoring Practices, manual
   activation, roving tabindex: exactly one tab is tabbable,
   Arrow Left/Right/Up/Down move and activate, Home/End jump to
   the ends, and there is no auto-rotation.
   ============================================================ */
(function () {
  "use strict";

  var FL = (window.FitLibrary = window.FitLibrary || {});
  if (!FL.register) return;

  /* ---- Explorer: ONE source of truth for the active family ----
     The controller binds to the SECTION, not to a single tablist, because the
     Explorer now has two navigation rows: the approved tablist above the panel
     and a secondary jump row below it. Both rows call the same select(), so
     there is exactly one active family and no parallel state.

     Selection is keyed by FAMILY rather than by button element, which is what
     lets any control carrying data-eb-family drive it.

     Keyboard model on the upper tablist is unchanged - WAI-ARIA Authoring
     Practices, manual activation, roving tabindex: exactly one tab is tabbable,
     Arrow Left/Right/Up/Down move and activate, Home/End jump to the ends, and
     there is no auto-rotation. */
  FL.register("EbookBundleExplorer", function (scope) {
    var root = scope || document;

    (root.querySelectorAll ? root.querySelectorAll("[data-eb-explorer]") : []).forEach(function (section) {
      if (section.dataset.ebBound === "true") return;
      section.dataset.ebBound = "true";

      var tablist = section.querySelector("[data-eb-tablist]");
      if (!tablist) return;

      var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      /* The lower row: plain buttons in a labelled group, never a second
         tablist. They carry aria-pressed and no id, so they can neither double
         the tab semantics nor collide with an existing id. */
      var jumps = Array.prototype.slice.call(section.querySelectorAll("[data-eb-jump]"));

      function panelFor(tab) {
        var id = tab.getAttribute("aria-controls");
        return id ? section.querySelector('[id="' + id + '"]') : null;
      }

      function familyOf(el) { return el.dataset.ebFamily; }

      /* The only place active state is ever written. */
      function select(family, opts) {
        opts = opts || {};
        var currentTab = null;
        var currentPanel = null;

        tabs.forEach(function (t) {
          var isCurrent = familyOf(t) === family;
          if (isCurrent) currentTab = t;
          t.setAttribute("aria-selected", isCurrent ? "true" : "false");
          t.setAttribute("tabindex", isCurrent ? "0" : "-1");
          var panel = panelFor(t);
          if (panel) {
            panel.hidden = !isCurrent;
            if (isCurrent) currentPanel = panel;
          }
        });

        /* The lower row mirrors that state - it never stores its own. */
        jumps.forEach(function (j) {
          j.setAttribute("aria-pressed", familyOf(j) === family ? "true" : "false");
        });

        if (opts.focusTab && currentTab) currentTab.focus();
        return currentPanel;
      }

      tabs.forEach(function (tab, index) {
        tab.addEventListener("click", function () { select(familyOf(tab)); });

        tab.addEventListener("keydown", function (event) {
          var next = null;
          switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
              next = tabs[(index + 1) % tabs.length];
              break;
            case "ArrowLeft":
            case "ArrowUp":
              next = tabs[(index - 1 + tabs.length) % tabs.length];
              break;
            case "Home":
              next = tabs[0];
              break;
            case "End":
              next = tabs[tabs.length - 1];
              break;
            default:
              return;
          }
          event.preventDefault();
          select(familyOf(next), { focusTab: true });
        });
      });

      /* Lower row: same select(), then put the reader at the START of the card
         they just asked for. Focus moves with preventScroll so the caret
         follows without the browser jumping, and the smooth scroll runs only on
         the narrow layout - where the panel stacks and the upper row is far
         off-screen, which is the problem this row exists to solve. Desktop is
         left alone, and reduced motion gets an instant jump. */
      jumps.forEach(function (jump) {
        jump.addEventListener("click", function () {
          var panel = select(familyOf(jump));
          if (!panel) return;

          panel.focus({ preventScroll: true });

          if (!window.matchMedia("(max-width: 980px)").matches) return;

          var offset = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue("--fl-sticky-offset"),
            10
          ) || 96;
          var top = panel.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: top, behavior: FL.reduceMotion() ? "auto" : "smooth" });
        });
      });

      /* Exactly one family is active even if the markup drifts. */
      var initial = tabs.filter(function (t) { return t.getAttribute("aria-selected") === "true"; })[0] || tabs[0];

      /* Optional deep link: #eb-explorer-routines opens that pack without
         scrolling anywhere on its own - the theme's anchor handler owns that. */
      var wanted = (window.location.hash || "").replace("#eb-explorer-", "");
      if (wanted && wanted.indexOf("#") === -1) {
        var match = tabs.filter(function (t) { return familyOf(t) === wanted; })[0];
        if (match) initial = match;
      }

      select(familyOf(initial));
    });
  });

  /* ---- Phase 2: FAQ accordion ------------------------------------------
     A separate registration, scoped per section instance. It shares nothing
     with the Explorer controller above, which is unchanged.

     The theme's global FAQ handler in fitlibrary-global.js binds `.faq__q`
     and would not match these buttons, so this is the only controller acting
     on them — no double-toggling. Items are independent: opening one never
     closes another, matching the approved page. No library is used. */
  FL.register("EbookBundleFaq", function (scope) {
    var root = scope || document;

    (root.querySelectorAll ? root.querySelectorAll("[data-eb-faq]") : []).forEach(function (section) {
      section.querySelectorAll("[data-eb-faq-toggle]").forEach(function (button) {
        if (button.dataset.ebBound === "true") return;
        button.dataset.ebBound = "true";

        var item = button.closest(".eb-faq");
        var panelId = button.getAttribute("aria-controls");
        var panel = panelId ? section.querySelector('[id="' + panelId + '"]') : null;

        button.setAttribute("aria-expanded", item && item.classList.contains("is-open") ? "true" : "false");

        button.addEventListener("click", function () {
          var open = !(item && item.classList.contains("is-open"));
          if (item) item.classList.toggle("is-open", open);
          button.setAttribute("aria-expanded", open ? "true" : "false");
          if (panel) panel.setAttribute("aria-hidden", open ? "false" : "true");
        });
      });
    });
  });
})();
